import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { creatureFor, pupilOffset } from "./creatures";
import "./trivia.css";

const GRID = 120;

/** A gentle look-around while nothing is calling for attention. */
function useWander(enabled) {
  const [gaze, setGaze] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let timer;
    const move = () => {
      const angle = Math.random() * Math.PI * 2;
      setGaze(Math.random() < 0.3 ? null : { dx: Math.cos(angle) * 50, dy: Math.sin(angle) * 50 });
      timer = setTimeout(move, 1800 + Math.random() * 2200);
    };
    timer = setTimeout(move, 600 + Math.random() * 1500);
    return () => clearTimeout(timer);
  }, [enabled]);
  return gaze;
}

/**
 * One trivia creature (projects/trivia): agy's sticker art with googly
 * eyes drawn over it. `look` is a point on the screen (client pixels) for
 * the pupils to follow; without one they wander. `mood` adds a pose:
 * "happy" hops with eyes up, "sad" droops cross-eyed, "wait" bobs slowly.
 *
 * The art's own eye whites stay in the picture; each eye is redrawn on
 * top (a white oval with an outline, just over the art's), so the pupil
 * sits inside it. A blink lays a lid in the body's colour over the eye.
 *
 * Decorative: hidden from screen readers. Whoever places it says who it
 * is in text.
 */
function Creature({ kind = 0, size = 96, look = null, mood = "idle", className = "" }) {
  const ref = useRef(null);
  const creature = creatureFor(kind);
  const wander = useWander(!look && mood === "idle");
  // A per-creature blink rhythm, so a crowd doesn't blink in unison.
  const [blinkDelay] = useState(() => `${(Math.random() * 4).toFixed(2)}s`);

  const rect = look ? ref.current?.getBoundingClientRect() : null;
  const scale = rect && rect.width ? GRID / rect.width : 1;

  function towards(eye, index) {
    if (mood === "happy") return { dx: 0, dy: -40 };
    if (mood === "sad") return { dx: index === 0 ? 40 : -40, dy: 20 };
    if (look && rect) {
      return {
        dx: (look.x - rect.left) * scale - eye.x,
        dy: (look.y - rect.top) * scale - eye.y,
      };
    }
    return wander;
  }

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${GRID} ${GRID}`}
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={`trivia-creature trivia-mood-${mood} ${className}`}
      data-creature={creature.name}
    >
      <g className="trivia-creature-body">
        <image href={creature.src} x="0" y="0" width={GRID} height={GRID} />
        {creature.eyes.map((eye, i) => {
          // Everything for an eye is drawn in the eye's own frame, tilted
          // to match the art (left eyes lean up to 13 degrees): an upright
          // oval left crescents of the art's outline around the lid.
          const turn = (eye.angle * Math.PI) / 180;
          const world = towards(eye, i);
          const local = world && {
            dx: world.dx * Math.cos(turn) + world.dy * Math.sin(turn),
            dy: -world.dx * Math.sin(turn) + world.dy * Math.cos(turn),
          };
          // The redrawn outline covers the art's: centred on its middle,
          // a little wider than it, so no second line shows.
          const stroke = eye.outline + 0.8;
          const ringX = eye.rx + eye.outline / 2;
          const ringY = eye.ry + eye.outline / 2;
          const innerX = ringX - stroke / 2;
          const innerY = ringY - stroke / 2;
          const pupil = Math.min(innerX, innerY) * 0.5;
          const { dx, dy } = pupilOffset(local, {
            x: Math.max(0, innerX - pupil - 1),
            y: Math.max(0, innerY - pupil - 1),
          });
          return (
            <g
              key={`eye-${i}`}
              data-eye=""
              transform={`rotate(${eye.angle} ${eye.x} ${eye.y})`}
            >
              <ellipse
                cx={eye.x}
                cy={eye.y}
                rx={ringX}
                ry={ringY}
                fill="var(--creature-eye)"
                stroke="var(--creature-ink)"
                strokeWidth={stroke}
              />
              <circle
                className="trivia-creature-pupil"
                cx={eye.x + dx}
                cy={eye.y + dy}
                r={pupil}
                fill="var(--creature-ink)"
              />
              <g className="trivia-creature-lid" style={{ animationDelay: blinkDelay }}>
                <ellipse
                  cx={eye.x}
                  cy={eye.y}
                  rx={ringX + stroke / 2 + 0.8}
                  ry={ringY + stroke / 2 + 0.8}
                  fill={creature.lid}
                />
                <path
                  d={`M${eye.x - innerX} ${eye.y} Q${eye.x} ${eye.y + innerY * 0.6} ${eye.x + innerX} ${eye.y}`}
                  fill="none"
                  stroke="var(--creature-ink)"
                  strokeWidth={1.6}
                  strokeLinecap="round"
                />
              </g>
            </g>
          );
        })}
      </g>
    </svg>
  );
}

Creature.propTypes = {
  kind: PropTypes.number,
  size: PropTypes.number,
  look: PropTypes.shape({ x: PropTypes.number, y: PropTypes.number }),
  mood: PropTypes.oneOf(["idle", "happy", "sad", "wait"]),
  className: PropTypes.string,
};

export default Creature;
